import type { ComponentProps } from 'react';
import React from 'react';
import type { HorizontalScrollRail } from '../HorizontalScrollRail';
import { useLoyaltyChallengeCardsRailDynamicProps } from './hooks';
import { useResponsive } from '../../hooks';
import type {
  LoyaltyChallengeCardsRailBaseProps,
  LoyaltyChallengeCardsRailEmptyStateProps,
} from './subcomponents';
import { LoyaltyChallengesEmptyState } from './subcomponents';
import { LoyaltyChallengeCardItem } from './subcomponents';
import { LoyaltyChallengeCardsRailBase } from './subcomponents';
import type { LoyaltyChallenge } from '../LoyaltyChallengeCard/LoyaltyChallengeCard.types';

export const LoyaltyChallengeCardsRail = (
  props: LoyaltyChallengeCardsRailProps,
) => {
  const {
    isLoading,
    headerText,
    headerVariation,
    withoutHeaderBorder,
    subtitle,
    challenges,
    emptyStateDescription,
    emptyStateImage,
  } = props;

  if (isLoading) {
    return (
      <LoyaltyChallengeCardsRailLoading
        headerText={headerText}
        headerVariation={headerVariation}
        withoutHeaderBorder={withoutHeaderBorder}
        subtitle={subtitle}
      />
    );
  }

  if (challenges.length === 0) {
    return (
      <LoyaltyChallengesEmptyState
        {...props}
        image={emptyStateImage}
        description={emptyStateDescription}
      />
    );
  }

  return <LoyaltyChallengeCardsRailFulfilled {...props} />;
};

const LoyaltyChallengeCardsRailLoading = (
  props: LoyaltyChallengeCardsRailBaseProps,
) => {
  const { railItemWidth, railOuterOffset, railItemGap } =
    useLoyaltyChallengeCardsRailDynamicProps();

  return (
    <LoyaltyChallengeCardsRailBase
      scrollEnabled={false}
      showNavControls={false}
      outerOffset={railOuterOffset}
      gap={railItemGap}
      {...props}
    >
      <LoyaltyChallengeCardItem isLoading={true} width={railItemWidth} />
      <LoyaltyChallengeCardItem isLoading={true} width={railItemWidth} />
      <LoyaltyChallengeCardItem isLoading={true} width={railItemWidth} />
    </LoyaltyChallengeCardsRailBase>
  );
};

const LoyaltyChallengeCardsRailFulfilled = (
  props: Omit<LoyaltyChallengeCardsRailProps, 'isLoading'>,
) => {
  const {
    availableCount,
    headerText,
    footerText,
    challenges,
    newBadgeText,
    onStartChallenge,
    onPressTermsAndConditions,
    terms,
    deadlinePrefixText,
    completionText,
    applyText,
    termsUrl,
    ...restProps
  } = props;

  const { minWidth } = useResponsive();
  const { railOuterOffset, railItemGap, railItemWidth } =
    useLoyaltyChallengeCardsRailDynamicProps();

  return (
    <LoyaltyChallengeCardsRailBase
      headerText={headerText}
      footerText={footerText}
      showNavControls={minWidth.tablet}
      outerOffset={railOuterOffset}
      gap={railItemGap}
      count={availableCount}
      {...restProps}
    >
      {challenges.map((challenge) => (
        <LoyaltyChallengeCardItem
          key={challenge.id}
          isLoading={false}
          width={railItemWidth}
          challenge={challenge}
          termsUrl={termsUrl}
          newBadgeText={newBadgeText}
          completionText={completionText}
          deadlinePrefixText={deadlinePrefixText}
          onStartChallenge={onStartChallenge}
          onPressTermsAndConditions={onPressTermsAndConditions}
          terms={terms}
          applyText={applyText}
        />
      ))}
    </LoyaltyChallengeCardsRailBase>
  );
};

// ─── TYPES ─────────────────────────────────────────────────────────────────

export type LoyaltyChallengeCardsRailProps = {
  readonly availableCount?: number;
  readonly isLoading?: boolean;
  readonly testID?: string;
  readonly terms: string;
  readonly termsUrl: string;
  readonly newBadgeText: string;
  readonly deadlinePrefixText: string;
  readonly completionText: string;
  readonly applyText: string;
  readonly emptyStateImage: LoyaltyChallengeCardsRailEmptyStateProps['image'];
  readonly emptyStateDescription: LoyaltyChallengeCardsRailEmptyStateProps['description'];
  readonly challenges: ReadonlyArray<
    Omit<LoyaltyChallenge, 'onStartChallenge' | 'terms' | 'applyText'>
  >;
} & Pick<LoyaltyChallengeCardsRailBaseProps, 'headerText' | 'footerText'> &
  Pick<LoyaltyChallenge, 'onStartChallenge' | 'onPressTermsAndConditions'> &
  Pick<
    ComponentProps<typeof HorizontalScrollRail>,
    'headerVariation' | 'withoutHeaderBorder' | 'subtitle'
  >;
